import TitleBar from '../../../components/TitleBar';
import { useEffect, useState } from 'react';
import { Button, Loading } from '../../../components';
import QuickEdit from '../../../components/assets/QuickEdit';
import { useNavigate, useParams } from 'react-router-dom';
import { FinalizeLocationBcp } from './forms';
import {
  ApolloError,
  gql,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  FIND_ALL_BCP_CORPORATES,
  FIND_SECTION_OWNER_BY_PLAN,
  UPDATE_SECTION_OWNER,
} from '../queries';
import { lastUpdateDateFormatter } from '../../../utils';
import { useUserContext } from '../../../context/PermissionsContext';
import BorderedSection from '../../../components/generic/BorderedSection';
import OwnersInformationLanding from './components/OwnersInformationLanding';
import AntModal from '../../../components/generic/AntModal';
import AdministrativeItem from './components/AdministrativeItem';
import { GET_DOCUMENT_LINKS_BY_LOCATION } from '../queries/documentLink';
import { message, notification } from 'antd';
import SectionOwnerForm from '../strategies/components/SectionOwnerForm';
import {
  BCPMission,
  BCPCrisis,
  BCPEmergency,
  BCPLocation,
  BCPStrategies,
  BCPprocessAreas,
  BCPActivation,
} from '../../../components/assets/BCP';
import RiskAppetite from './components/RiskAppetite';
import {
  FETCH_LOCATION_BY_ID,
  UPDATE_MTO_RTO,
} from '../../dataInputs/queries/locations';
import { NotificationPlacement } from 'antd/es/notification/interface';
import { DocumentLinks } from '../strategies/components';
import { FindAllBcpCorporates } from '../../../interfaces/bcp';
import QueryErrorBoundary from '../../../components/error-handling';
import TitleUpdater from '../../../components/common/TitleUpdater';

export type FindSectionOwnerByPlan = {
  sectionOwnerfirstName: string;
  sectionOwnerlastName: string;
  email: string;
  uuid?: string;
  sectionRequired?: boolean;
};

interface DataReturned {
  findSectionOwnerByPlan: FindSectionOwnerByPlan;
}

export const FETCH_PDF = gql`
  query ExportBusinessContinuityPlanPdf($uuid: ID!) {
    exportBusinessContinuityPlanPdf(uuid: $uuid)
  }
`;

export default function BCPLanding() {
  const { isAdmin, canEdit, canExport, canApprove } = useUserContext();
  const params = useParams();
  const navigate = useNavigate();
  const { type, locationId } = params;
  const [modalFinalizeOpen, setModalFinalizeOpen] = useState(false);
  const [modalEditOpen, setModalEditOpen] = useState(false);
  const [isRiskAppetiteOpen, setIsRiskAppetiteOpen] = useState(false);
  const [mtoRtoError, setMtoRtoError] = useState<ApolloError | null>(null);

  const [
    fetchPDF,
    { data: pdfGenerated, loading: pdfLoading, error: pdfError },
  ] = useLazyQuery(FETCH_PDF);

  const isLocationType = type === 'location';

  const {
    data: locationData,
    loading: locationLoading,
    error: locationError,
    refetch: refetchLocation,
  } = useQuery(FETCH_LOCATION_BY_ID, {
    variables: { uuid: locationId },
    skip: !isLocationType || !locationId,
  });

  const { locationName, mtoMonth, mtoRevenue, rtoMonth, rtoRevenue } =
    locationData?.findLocationByUuid || {};

  const { data: corporateData, loading: corporateLoading } =
    useQuery<FindAllBcpCorporates>(FIND_ALL_BCP_CORPORATES, {
      skip: isLocationType || !locationId,
    });

  const { planUUID, bcpStatus, lastBcpUpdate } =
    locationData?.findLocationByUuid ||
    corporateData?.findAllBcpCorporates[0] ||
    {};

  const { planUUID: corporatePlanUUID, lastBcpUpdate: corporateLastBcpUpdate } =
    corporateData?.findAllBcpCorporates[0] || {};

  const LANDING_LOCATION_SLUG = `/${type}/${locationId}`;

  const [messageApi, contextHolder] = message.useMessage();
  const [api, notificationContextHolder] = notification.useNotification();
  const [lastUpdate, setLastUpdate] = useState(lastBcpUpdate);

  const { data, loading, error } = useQuery<DataReturned>(
    FIND_SECTION_OWNER_BY_PLAN,
    {
      variables: { uuid: planUUID || corporatePlanUUID },
      skip: !planUUID && !corporatePlanUUID,
    },
  );

  const [updateRiskAppetite, { loading: updateLocationLoading }] = useMutation(
    UPDATE_MTO_RTO,
    {
      onError: (error) => {
        setMtoRtoError(error);
        messageApi.open({
          type: 'error',
          content: error.message,
        });
      },
    },
  );

  const handleMTORTOSubmit = async (data: any) => {
    const submitData = {
      rtoMonth:
        data.RecoveryTimeObjectiveMto === '--'
          ? 0
          : parseFloat(data.RecoveryTimeObjectiveMto),
      mtoRevenue: parseFloat(data.MaxTolerableOutageRto),
    };

    try {
      const response = await updateRiskAppetite({
        variables: {
          data: {
            uuid: locationId,
            ...submitData,
          },
        },
      });
      if (response.data) {
        setIsRiskAppetiteOpen(false);
        refetchLocation();
        messageApi.open({
          type: 'success',
          content: 'Changes Saved',
        });
      }
    } catch (error) {
      console.log('error', error);
      messageApi.open({
        type: 'error',
        content: 'Operation Failed',
      });
    }
  };

  const [
    updateSectionOwner,
    { loading: updateSOLoading, error: updateSOError },
  ] = useMutation(UPDATE_SECTION_OWNER, {
    refetchQueries: [
      {
        query: FIND_SECTION_OWNER_BY_PLAN,
        variables: { uuid: planUUID },
      },
    ],
  });

  const onFinish = async (
    formData: FindSectionOwnerByPlan,
    isDirty: boolean,
  ) => {
    // if (!isDirty) return setModalEditOpen(false);
    try {
      const response = await updateSectionOwner({
        variables: {
          data: {
            uuid: data?.findSectionOwnerByPlan?.uuid!,
            ...formData,
            sectionRequired: formData?.sectionRequired?.toString() === 'yes',
          },
        },
      });
      if (response.data) {
        setModalEditOpen(false);
        messageApi.open({
          type: 'success',
          content: 'Changes Saved',
        });
      }
    } catch (error) {
      console.log('error', error);
      messageApi.open({
        type: 'error',
        content: 'Operation Failed',
      });
    }
  };

  const {
    data: documentLinks,
    loading: documentLinksLoading,
    error: documentLinksError,
  } = useQuery(GET_DOCUMENT_LINKS_BY_LOCATION, {
    variables: {
      uuid: locationId,
    },
  });

  const documentLinksArray = documentLinks?.findAllByLocation || [];

  const handleCloseSectionOwner = () => {
    setModalEditOpen(false);
  };

  const handleCloseFinalize = (updatedValue: string) => {
    setLastUpdate(updatedValue);
    setModalFinalizeOpen(false);
  };

  const handleCloseRiskAppetiteModal = () => {
    setMtoRtoError(null);
    setIsRiskAppetiteOpen(false);
  };

  const handlePdfGeneration = async () => {
    if (!locationId) return;

    openNotification('topRight');

    fetchPDF({
      variables: { uuid: locationId },
    });

    if (pdfGenerated) api.success({ message: 'PDF generated successfully' });

    if (pdfError)
      api.error({
        message: 'Failed to generate PDF',
        description: pdfError?.message,
      });
  };

  const openNotification = (placement: NotificationPlacement) => {
    api.info({
      message: `Your PDF is being generated`,
      description:
        'Please wait a moment while we generate your PDF. This action may take a few minutes. You can continue to use the platform while we generate your PDF, and we will notify you via email once it is ready.',
      placement,
      duration: 10,
    });
  };

  useEffect(() => {
    setLastUpdate(isLocationType ? lastBcpUpdate : corporateLastBcpUpdate);
  }, [locationData]);

  useEffect(() => {
    if (!lastUpdate) return;
    refetchLocation();
  }, [modalFinalizeOpen]);

  return (
    <>
      {contextHolder}
      {notificationContextHolder}
      <TitleUpdater
        title={
          !isLocationType ? 'Corporate BCP Hub' : `${locationName} BCP Hub`
        }
      />
      <>
        {!isLocationType && (
          <TitleBar
            title="BCP Hub"
            subtitle="Corporate"
            brief="At a corporate level, the following section need to be populated for the BCP to be effective. Note that these items are not specific to your corporate headquarters, which will receive its own BCP."
          />
        )}
        {isLocationType && (
          <TitleBar
            title="BCP Hub"
            subtitle={locationName}
            brief="At a location level, the following section need to be populated for the BCP to be effective."
          />
        )}
        <section
          className={`w-full h-full ${
            isLocationType ? 'grid' : 'flex flex-col max-w-4xl mx-auto'
          } xl:grid-cols-2 gap-4 mb-6`}
        >
          <QueryErrorBoundary error={error}>
            <BorderedSection
              title={`${isLocationType ? 'Location' : 'Corporate'} BCP Owner`}
            >
              <OwnersInformationLanding
                data={data?.findSectionOwnerByPlan!}
                setModalEditOpen={setModalEditOpen}
                isLocationType={isLocationType}
                loading={locationLoading || corporateLoading || loading}
              />
            </BorderedSection>
          </QueryErrorBoundary>
          {isLocationType && (
            <QueryErrorBoundary error={locationError}>
              <BorderedSection title="Risk Appetite">
                <RiskAppetite
                  type="summary"
                  mtoMonth={mtoMonth}
                  mtoRevenue={mtoRevenue}
                  rtoMonth={rtoMonth}
                  rtoRevenue={rtoRevenue}
                  loading={locationLoading}
                />
                <div className="absolute right-3 top-3">
                  {(isAdmin || canEdit) && (
                    <Button
                      type="edit"
                      onClick={() => setIsRiskAppetiteOpen(true)}
                    >
                      <QuickEdit />
                    </Button>
                  )}
                </div>
              </BorderedSection>
            </QueryErrorBoundary>
          )}
          <BorderedSection
            title={`${
              isLocationType ? 'Administrative Items' : 'Corporate Items'
            } `}
          >
            {(isLocationType && !locationLoading) ||
            (!isLocationType && !corporateLoading) ? (
              <>
                <div className="absolute left-0 right-0 top-0 mb-4 py-1.5 px-6   text-primary-100 tracking-widest text-right">
                  <span className="text-sm font-normal mr-1">
                    LAST UPDATE //
                  </span>
                  <span className="font-bold text-sm">
                    {bcpStatus
                      ? lastUpdateDateFormatter(lastBcpUpdate)
                      : 'NEVER'}
                  </span>
                </div>
                <div className="mt-10 flex flex-col gap-3">
                  <AdministrativeItem
                    name="Mission Statement & Scope"
                    Icon={BCPMission}
                    slug={`/mission-scope${LANDING_LOCATION_SLUG}`}
                  />
                  <AdministrativeItem
                    name="Plan Activation Criteria"
                    Icon={BCPActivation}
                    slug={`/plan-activation${LANDING_LOCATION_SLUG}`}
                  />
                  <AdministrativeItem
                    name="Location Crisis Management Team"
                    Icon={BCPLocation}
                    slug={`/crisis-management${LANDING_LOCATION_SLUG}`}
                  />
                  <AdministrativeItem
                    name="Crisis Communication"
                    Icon={BCPCrisis}
                    slug={`/crisis-communication${LANDING_LOCATION_SLUG}`}
                  />
                  {!isLocationType && (
                    <AdministrativeItem
                      name="IT Disaster Recovery Plans"
                      Icon={BCPprocessAreas}
                      slug={`/disaster-recovery${LANDING_LOCATION_SLUG}`}
                    />
                  )}
                  <AdministrativeItem
                    name="Emergency Response"
                    Icon={BCPEmergency}
                    slug={`/emergency-response${LANDING_LOCATION_SLUG}`}
                  />
                </div>

                {canApprove && (
                  <Button
                    type="primary"
                    className="mt-6 w-[237px] mx-auto text-sm"
                    onClick={() => setModalFinalizeOpen(true)}
                  >
                    Refresh Admin Items
                  </Button>
                )}
              </>
            ) : (
              <Loading />
            )}
          </BorderedSection>
          {isLocationType && (
            <section className="flex flex-col flex-1 gap-4">
              <BorderedSection title="Mitigation Strategies">
                {isLocationType && (
                  <div className="flex flex-col gap-3">
                    <AdministrativeItem
                      name="Location Strategies"
                      Icon={BCPStrategies}
                      slug={`/strategies/location/${locationId}`}
                    />
                    <AdministrativeItem
                      name="Process Area & Process Strategies"
                      Icon={BCPprocessAreas}
                      slug={`/process-areas-and-strategies/location/${locationId}`}
                    />
                  </div>
                )}
              </BorderedSection>
              <QueryErrorBoundary error={documentLinksError}>
                <DocumentLinks
                  data={documentLinksArray}
                  parentUuid={locationId}
                  queryToUpdate="FindAllByLocation"
                  model="location"
                />
              </QueryErrorBoundary>
            </section>
          )}
        </section>

        {isLocationType && canExport && (
          <Button
            type="primary"
            className={`text-sm w-[330px] my-2 py-4 ml-auto h-full flex justify-center text-center ${
              pdfLoading ? 'cursor-not-allowed' : ''
            }`}
            disabled={pdfLoading}
            onClick={handlePdfGeneration}
          >
            Export BCP
          </Button>
        )}
        <AntModal
          title="Section Owner"
          openModal={modalEditOpen}
          onCancel={handleCloseSectionOwner}
        >
          <QueryErrorBoundary error={updateSOError}>
            <SectionOwnerForm
              data={data?.findSectionOwnerByPlan!}
              onFinish={onFinish}
              loading={updateSOLoading}
              type="location"
            />
          </QueryErrorBoundary>
        </AntModal>
        <AntModal
          openModal={isRiskAppetiteOpen}
          onCancel={handleCloseRiskAppetiteModal}
          title="Location MTO & RTO"
          height={520}
        >
          <QueryErrorBoundary error={locationError}>
            <RiskAppetite
              type="modal"
              mtoMonth={mtoMonth}
              mtoRevenue={mtoRevenue}
              rtoMonth={rtoMonth}
              rtoRevenue={rtoRevenue}
              onFinish={handleMTORTOSubmit}
              loading={updateLocationLoading}
            />
          </QueryErrorBoundary>
        </AntModal>
        <AntModal
          openModal={modalFinalizeOpen}
          onCancel={() => setModalFinalizeOpen(false)}
          title={`Refresh ${isLocationType ? 'Location' : 'Corporate'} Items`}
        >
          <FinalizeLocationBcp
            uuid={isLocationType ? planUUID : corporatePlanUUID}
            onClose={handleCloseFinalize}
            lastUpdate={bcpStatus ? lastBcpUpdate : 'NEVER'}
            isCorporate={!isLocationType}
          />
        </AntModal>
        <Button
          type="primary"
          className="w-[188px] my-2 text-sm"
          onClick={() => navigate('/bcp-hub')}
        >
          Back
        </Button>
      </>
    </>
  );
}
